import {Component, inject, ViewChild, DestroyRef, signal} from '@angular/core';
import {CdkMenu, CdkMenuItem, CdkMenuTrigger} from '@angular/cdk/menu';
import {RouterLink} from '@angular/router';
import {AsyncPipe} from '@angular/common';
import {IdfSelectComponent} from "../idf-select/idf-select.component";
import {MatBadgeModule} from '@angular/material/badge';
import {MatIconModule} from '@angular/material/icon';
import {UserService} from "../../core/user/services/user.service";
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {UserModel} from "../../core/user/user.model";
import {environment} from "../../../environments/environment";
import {CartService} from "../../core/sales/services/cart.service";
import {ReturnConditionsService} from "../../private/return-conditions/services/return-conditions.service";

@Component({
  selector: 'app-navbar',
  imports: [CdkMenu, CdkMenuItem, CdkMenuTrigger, RouterLink, IdfSelectComponent, MatBadgeModule, MatIconModule, AsyncPipe],
  templateUrl: './navbar.component.html',
  standalone: true,
  styleUrls: ['./navbar.component.scss']
})

export class NavbarComponent {
  private destroyRef = inject(DestroyRef);
  protected userService: UserService = inject(UserService);
  protected hasConditions = inject(ReturnConditionsService).hasConditions;
  private cartService: CartService = inject(CartService);

  mobileMenuOpen = signal(false);
  dataSectionOpen = signal(false);
  ordersSectionOpen = signal(false);
  servicesSectionOpen = signal(false);
  settingsSectionOpen = signal(false);
  userSectionOpen = signal(false);
  internSectionOpen = signal(false);
  @ViewChild('dataTrigger', {read: CdkMenuTrigger}) dataTrigger?: CdkMenuTrigger;
  @ViewChild('ordersTrigger', {read: CdkMenuTrigger}) ordersTrigger?: CdkMenuTrigger;
  @ViewChild('servicesTrigger', {read: CdkMenuTrigger}) servicesTrigger?: CdkMenuTrigger;
  @ViewChild('settingsTrigger', {read: CdkMenuTrigger}) settingsTrigger?: CdkMenuTrigger;
  @ViewChild('userTrigger', {read: CdkMenuTrigger}) userTrigger?: CdkMenuTrigger;
  @ViewChild('internTrigger', {read: CdkMenuTrigger}) internTrigger?: CdkMenuTrigger;

  private closeTimeout?: number;

  protected userName: any;
  public user: UserModel | null = null;

  protected cartCount: any = 0;

  constructor(
  ) {
    this.userService.getUserResponseObservable().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((userResponse: any) => {
      if (userResponse !== undefined) {
        this.userName = userResponse.name;
        this.user = userResponse.user;
      }
    });

    this.cartService.cartItems$.subscribe(items=>{
      this.cartCount = items.length;
      //console.log("CartCount: " + this.cartCount);
    })
  }

  toggleMobileMenu() {
    this.mobileMenuOpen.set(!this.mobileMenuOpen());
  }

  closeMobileMenu() {
    this.mobileMenuOpen.set(false);
  }

  openDataMenu() {
    this.clearCloseTimeout();
    this.dataTrigger?.open();
  }

  closeDataMenu() {
    this.closeTimeout = window.setTimeout(() => {
      this.dataTrigger?.close();
    }, 200);
  }

  protected toggleDataSection() {
    this.dataSectionOpen.set(!this.dataSectionOpen());
  }

  openOrdersMenu() {
    this.clearCloseTimeout();
    this.ordersTrigger?.open();
  }

  closeOrdersMenu() {
    this.closeTimeout = window.setTimeout(() => {
      this.ordersTrigger?.close();
    }, 200);
  }

  protected toggleOrdersSection() {
    this.ordersSectionOpen.set(!this.ordersSectionOpen());
  }

  openServicesMenu() {
    this.clearCloseTimeout();
    this.servicesTrigger?.open();
  }

  closeServicesMenu() {
    this.closeTimeout = window.setTimeout(() => {
      this.servicesTrigger?.close();
    }, 200);
  }

  protected toggleServicesSection() {
    this.servicesSectionOpen.set(!this.servicesSectionOpen());
  }

  openSettingsMenu() {
    this.clearCloseTimeout();
    this.settingsTrigger?.open();
  }

  closeSettingsMenu() {
    this.closeTimeout = window.setTimeout(() => {
      this.settingsTrigger?.close();
    }, 200);
  }

  protected toggleSettingsSection() {
    this.settingsSectionOpen.set(!this.settingsSectionOpen());
  }

  openUserMenu() {
    this.clearCloseTimeout();
    this.userTrigger?.open();
  }

  closeUserMenu() {
    this.closeTimeout = window.setTimeout(() => {
      this.userTrigger?.close();
    }, 200);
  }

  protected toggleUserSection() {
    this.userSectionOpen.set(!this.userSectionOpen());
  }

  openInternMenu() {
    this.clearCloseTimeout();
    this.internTrigger?.open();
  }

  closeInternMenu() {
    this.closeTimeout = window.setTimeout(() => {
      this.internTrigger?.close();
    }, 200);
  }

  protected toggleInternSection() {
    this.internSectionOpen.set(!this.internSectionOpen());
  }

  private clearCloseTimeout() {
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
      this.closeTimeout = undefined;
    }
  }

  canAccessTile(authorities: any[]) {
    if (this.user) {
      if (this.userService.isAdmin()) {
        return true;
      }

      if (this.userService.isOwner()) {
        if (authorities.includes('PREISLISTE')) {
          if (!(this.user.authoritiesEnum.filter(value => authorities.includes(value)).length > 0)) {
            return false;
          }
        }
        return true;
      }

      if (this.user.authoritiesEnum.filter(value => authorities.includes(value)).length > 0) {
        return true;
      }
    }
    return false;
  }

  isQa(): boolean {
    return environment.qa;
  }

  isDev(): boolean {
    return environment.dev;
  }

  isLocal(): boolean {
    return !(environment.dev || environment.production || environment.qa);
  }

  isDebugMode(): boolean {
    return environment.isDebugMode;
  }
}
